// おすすめ商品センター（右下FAB＋モーダル）の制御。
// カート内の商品IDと各ブロックの「トリガー商品ID」を突き合わせ、
// 合致した枠だけを表示します。合致が0件（カートが空を含む）ならFABごと非表示。
// 設定は window.RecommendConfig 経由で受け取ります。
//
// テーマエディタではセクションが差し替え描画されるため、イベントは document への
// 委譲で登録し、DOM参照は setup() で取り直す構造にしています。
(() => {
  const dom = { wrap: null, overlay: null, fab: null, blocks: [], zoomOverlay: null, zoomImage: null, addBtn: null, cartLink: null };
  let config = {};
  let triggerMap = new Map();

  const selectedQty = {}; // { variantId: qty }
  let cartProductIds = new Set();
  let cartItemCount = 0;
  // 直前のカート内容。「今回何が増えたか」を知るために保持する。
  // これが無いと、対象商品が既にカートに居るかぎり無関係な追加でもモーダルが開く。
  let lastCartQty = new Map();
  let addedProductIds = new Set();
  // モーダル自身が発行したカート追加は自動オープンの対象外にする（fetchフックの
  // デバウンス分を含めて余裕を持たせたいため、カウンタではなく時刻で抑制する）
  let suppressAutoOpenUntil = 0;

  const rootUrl = () =>
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

  // テーマエディタのプレビュー中は true。カートが空でも中身を確認できるようにする。
  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* -------------------------------------------------
   * 表示状態の計算
   * ------------------------------------------------- */
  function blockMatches(blockId) {
    // テーマエディタではカートが空のことが多いため、きっかけの判定を飛ばして全枠を見せる
    if (isDesignMode()) return true;
    const trigger = triggerMap.get(blockId);
    if (!trigger) return false;
    if (trigger.type === 'any') return cartItemCount > 0;
    for (const id of trigger.ids) {
      if (cartProductIds.has(id)) return true;
    }
    return false;
  }

  // 枠の出し分け → FABの表示、をまとめて行い、実際に表示された枠のIDを返す。
  // 商品行はカートの中身に関係なく常に出したままにする（一度追加した商品も
  // 買い足せるように、また追加した瞬間に枠やFABが消えないようにするため）。
  function render() {
    if (!dom.wrap) return [];

    // モーダルを開いている間は中身を組み替えない。
    // （操作中に枠が増減して、ボタン位置がずれるのを防ぐ）
    if (dom.overlay && !dom.overlay.hidden) {
      return dom.blocks.filter((blockEl) => !blockEl.hidden).map((blockEl) => blockEl.getAttribute('data-block-id'));
    }

    const visibleBlockIds = [];

    dom.blocks.forEach((blockEl) => {
      const show = blockMatches(blockEl.getAttribute('data-block-id'));
      blockEl.hidden = !show;
      // 非表示になった枠の選択数量は持ち越さない
      if (!show) clearBlockSelection(blockEl);
      if (show) visibleBlockIds.push(blockEl.getAttribute('data-block-id'));
    });

    syncQuantities();

    dom.wrap.hidden = visibleBlockIds.length === 0;
    if (window.FabRotator) window.FabRotator.refresh();
    if (dom.wrap.hidden) close();

    return visibleBlockIds;
  }

  function applyCart(cart) {
    const items = (cart && cart.items) || [];
    cartProductIds = new Set(items.map((item) => item.product_id));
    cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const nextQty = new Map();
    items.forEach((item) => {
      nextQty.set(item.product_id, (nextQty.get(item.product_id) || 0) + item.quantity);
    });
    addedProductIds = new Set();
    nextQty.forEach((qty, productId) => {
      if (qty > (lastCartQty.get(productId) || 0)) addedProductIds.add(productId);
    });
    lastCartQty = nextQty;

    return render();
  }

  function fetchCart() {
    return fetch(rootUrl() + 'cart.js', { cache: 'no-store' })
      .then((res) => res.json())
      .catch(() => null);
  }

  /* -------------------------------------------------
   * カート追加の検知
   * Dawn の pubsub だけでは PageFly 等からの追加を拾えないため、
   * fetch / XHR も監視して /cart/add への POST 完了をトリガーにする
   * ------------------------------------------------- */
  let refreshTimer = null;
  function scheduleRefresh(fromAdd) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      fetchCart().then((cart) => {
        if (!cart) return;
        const visibleBlockIds = applyCart(cart);
        if (fromAdd) handleAddedFeedback(visibleBlockIds);
      });
    }, 300);
  }

  // 今回増えた商品が、その枠のきっかけに当てはまるか
  function triggeredByAddition(blockId) {
    const trigger = triggerMap.get(blockId);
    if (!trigger) return false;
    if (trigger.type === 'any') return addedProductIds.size > 0;
    for (const productId of addedProductIds) {
      if (trigger.ids.has(productId)) return true;
    }
    return false;
  }

  function handleAddedFeedback(visibleBlockIds) {
    if (!dom.wrap || visibleBlockIds.length === 0) return;

    // 吹き出しを「追加直後だけ」出すモード
    if (config.bubbleDisplay === 'after_add') {
      dom.wrap.classList.add('is-bubble-shown');
      setTimeout(() => dom.wrap && dom.wrap.classList.remove('is-bubble-shown'), 6000);
    }

    if (config.openBehavior !== 'auto') return;
    if (Date.now() < suppressAutoOpenUntil) return;
    // 特典センターがプレゼントを自動付与した直後は、お客様の操作ではないので開かない
    if (Date.now() < (window.__perkGiftSyncUntil || 0)) return;

    // 「カートにきっかけ商品が居る」だけでは開かない。今回それが増えたときだけ開く。
    if (!visibleBlockIds.some(triggeredByAddition)) return;

    // 特典センターと同時に成立した場合、どちらを開くかは特典センター側の設定に従う
    if (window.__perkAutoOpenPriority === 'perk' && Date.now() < (window.__perkAutoOpenUntil || 0)) return;
    window.__recommendAutoOpenUntil = Date.now() + (Number(config.autoOpenDelay) || 0) + 800;

    setTimeout(open, Number(config.autoOpenDelay) || 0);
  }

  const CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?|$)/;
  function isCartAddUrl(url) {
    if (!url) return false;
    return CART_ADD_PATTERN.test(String(url).split('#')[0]);
  }

  function installNetworkHooks() {
    // テーマ標準の仕組みを通らないカート追加（PageFly製LP等）まで拾うかは設定で切り替える
    if (!config.pageflyDetect) return;
    if (window.__recommendHooksInstalled) return;
    window.__recommendHooksInstalled = true;

    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        const promise = originalFetch.apply(this, args);
        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input && input.url;
          if (isCartAddUrl(url)) {
            promise
              .then((res) => {
                if (res && res.ok) scheduleRefresh(true);
              })
              .catch(() => {});
          }
        } catch (e) {
          // URLが読めない形式でも本来の fetch は壊さない
        }
        return promise;
      };
    }

    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__recommendUrl = url;
      return originalXhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (isCartAddUrl(this.__recommendUrl)) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) scheduleRefresh(true);
        });
      }
      return originalXhrSend.apply(this, arguments);
    };
  }

  /* -------------------------------------------------
   * モーダル開閉
   * ------------------------------------------------- */
  function open() {
    if (!dom.wrap || !dom.overlay || dom.wrap.hidden) return;
    dom.overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    if (dom.fab) dom.fab.classList.add('recommend-fab--seen');
  }
  function close() {
    if (!dom.overlay || dom.overlay.hidden) return;
    dom.overlay.hidden = true;
    document.body.style.overflow = '';
    // 開いている間は凍結していた表示状態を、閉じたタイミングで最新に合わせる
    render();
  }
  function closeZoom() {
    if (dom.zoomOverlay) dom.zoomOverlay.hidden = true;
  }

  /* -------------------------------------------------
   * 数量ステッパー
   * ------------------------------------------------- */
  // 表示中の枠すべての選択数量。ボタンは1組なのでモーダル全体で合計する。
  function totalSelected() {
    let total = 0;
    dom.blocks.forEach((blockEl) => {
      if (blockEl.hidden) return;
      blockEl.querySelectorAll('[data-recommend-row]').forEach((row) => {
        total += selectedQty[row.getAttribute('data-variant-id')] || 0;
      });
    });
    return total;
  }

  // 数量はバリアント単位で共有している。同じ商品が複数の枠に出ていても
  // 表示が食い違わないよう、変更時は全枠の数字とフッターをまとめて更新する。
  function syncQuantities() {
    dom.blocks.forEach((blockEl) => {
      blockEl.querySelectorAll('[data-recommend-row]').forEach((row) => {
        const qtyEl = row.querySelector('[data-recommend-qty]');
        if (qtyEl) qtyEl.textContent = selectedQty[row.getAttribute('data-variant-id')] || 0;
      });
    });
    // 追加完了の表示を出したまま数量が選ばれたら、通常の追加ボタンに戻す
    if (isAdded() && totalSelected() > 0) setAddedState(false);
    updateAddButton();
  }

  // バリエーションの切り替え。数量はバリアントIDをキーに保持しているため、
  // 選択中の数量を新しいバリエーションへ引き継いでから行の中身を差し替える。
  function selectVariant(rowEl, source) {
    const nextId = source.getAttribute('data-recommend-variant') || source.value;
    const currentId = rowEl.getAttribute('data-variant-id');
    if (!nextId || nextId === currentId) return;

    const carried = selectedQty[currentId] || 0;
    delete selectedQty[currentId];
    if (carried > 0) selectedQty[nextId] = carried;
    rowEl.setAttribute('data-variant-id', nextId);

    const priceEl = rowEl.querySelector('[data-recommend-price]');
    const price = source.getAttribute('data-price');
    if (priceEl && price) priceEl.textContent = price;

    const imageEl = rowEl.querySelector('[data-recommend-image]');
    const image = source.getAttribute('data-image');
    if (imageEl && image) imageEl.src = image;

    // 拡大表示もそのバリエーションの画像に合わせる
    const zoom = source.getAttribute('data-zoom');
    if (zoom) {
      rowEl.querySelectorAll('[data-recommend-zoom]').forEach((el) => el.setAttribute('data-recommend-zoom', zoom));
    }

    // チップの選択状態（プルダウンはブラウザが管理するため対象外）
    rowEl.querySelectorAll('[data-recommend-variant]').forEach((chip) => {
      const isSelected = chip === source;
      chip.classList.toggle('is-selected', isSelected);
      chip.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    syncQuantities();
  }

  function clearBlockSelection(blockEl) {
    blockEl.querySelectorAll('[data-recommend-row]').forEach((row) => {
      delete selectedQty[row.getAttribute('data-variant-id')];
    });
  }

  function resetSelection() {
    dom.blocks.forEach(clearBlockSelection);
    syncQuantities();
  }

  function updateAddButton() {
    if (dom.addBtn) dom.addBtn.disabled = totalSelected() < 1;
  }

  // 追加完了の状態。ボタンを「カートに追加しました」のまま保持し、
  // その下に「カートへ移動」を出す。次に数量が選ばれた時点で通常状態へ戻す。
  const isAdded = () => Boolean(dom.overlay && dom.overlay.classList.contains('is-added'));

  function setAddedState(added) {
    if (!dom.overlay) return;
    dom.overlay.classList.toggle('is-added', added);
    if (dom.addBtn) {
      dom.addBtn.textContent = added
        ? config.addedLabel || 'カートに追加しました！'
        : dom.addBtn.dataset.defaultLabel || dom.addBtn.textContent;
    }
    if (dom.cartLink) dom.cartLink.hidden = !added;
  }

  /* -------------------------------------------------
   * カートに追加
   * ------------------------------------------------- */
  function updateCartIconBubble(sections) {
    const html = sections && sections['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    if (!html || !target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (parsed) target.innerHTML = parsed.innerHTML;
  }

  function showError(message) {
    // 標準の alert はブラウザ操作を止めてしまうため、ボタン直下に短く出す
    if (!dom.addBtn) return;
    let errorEl = dom.overlay.querySelector('[data-recommend-error]');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'recommend-block__note recommend-block__note--error';
      errorEl.setAttribute('data-recommend-error', '');
      dom.addBtn.insertAdjacentElement('afterend', errorEl);
    }
    errorEl.textContent = message;
  }

  function clearError() {
    const errorEl = dom.overlay && dom.overlay.querySelector('[data-recommend-error]');
    if (errorEl) errorEl.remove();
  }

  // 計測用のラインアイテムプロパティ。ここで追加した商品だけに付き、
  // 注文まで引き継がれるため、購入後の集計に使える。
  // 値は枠ごとの設定なので、施策別に売上を分けられる。
  // キーの先頭がアンダースコアのものは顧客向け表示から除外される。
  function attributionProperties(blockEl) {
    if (!config.attributionEnabled) return null;
    const key = config.attributionKey;
    const value = blockEl.dataset.attributionValue;
    if (!key || !value) return null;
    const props = {};
    props[key] = value;
    return props;
  }

  function submitAll() {
    const items = [];
    dom.blocks.forEach((blockEl) => {
      if (blockEl.hidden) return;
      const props = attributionProperties(blockEl);
      blockEl.querySelectorAll('[data-recommend-row]').forEach((row) => {
        const variantId = row.getAttribute('data-variant-id');
        const quantity = selectedQty[variantId] || 0;
        if (quantity > 0) {
          const item = { id: variantId, quantity: quantity };
          if (props) item.properties = props;
          items.push(item);
        }
      });
    });
    if (items.length === 0) return;

    const addBtn = dom.addBtn;
    addBtn.disabled = true;
    addBtn.textContent = config.addingLabel || '追加中...';
    // 自分の追加リクエストが fetch フックを経由して自動オープンを誘発しないようにする
    suppressAutoOpenUntil = Date.now() + 5000;

    fetch((config.cartAddUrl || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: ['cart-icon-bubble'] }),
    })
      .then((res) =>
        res.json().then((data) => {
          if (!res.ok) throw new Error(data.description || 'カートへの追加に失敗しました。');
          return data;
        })
      )
      .then((data) => {
        updateCartIconBubble(data.sections);

        // カートページでは行の再描画が必要なためリロードする
        if (document.querySelector('cart-items')) {
          window.location.reload();
          return null;
        }

        clearError();
        // 数量は0に戻すが、完了表示は自動では消さない（次に数量が選ばれるまで保持）
        resetSelection();
        setAddedState(true);

        return fetchCart().then((cart) => {
          if (cart) applyCart(cart);
        });
      })
      .catch((err) => {
        setAddedState(false);
        updateAddButton();
        showError(err.message || 'カートへの追加に失敗しました。時間をおいて再試行してください。');
      });
  }

  /* -------------------------------------------------
   * イベント（document への委譲。登録は1回だけ）
   * ------------------------------------------------- */
  function installListeners() {
    if (window.__recommendListenersInstalled) return;
    window.__recommendListenersInstalled = true;

    document.addEventListener('click', (e) => {
      if (!dom.wrap || !dom.overlay) return;
      if (!(e.target instanceof Element)) return;

      // FAB・吹き出しから開く
      if (dom.wrap.contains(e.target) && e.target.closest('[data-recommend-open]')) {
        open();
        return;
      }

      // 画像・商品名タップでの拡大表示
      const zoomBtn = e.target.closest('[data-recommend-zoom]');
      if (zoomBtn && dom.overlay.contains(zoomBtn)) {
        if (dom.zoomOverlay && dom.zoomImage) {
          dom.zoomImage.src = zoomBtn.getAttribute('data-recommend-zoom');
          dom.zoomOverlay.hidden = false;
        }
        return;
      }
      if (dom.zoomOverlay && dom.zoomOverlay.contains(e.target)) {
        closeZoom();
        return;
      }

      // 閉じる（×ボタン／背景タップ）
      if (e.target === dom.overlay || (dom.overlay.contains(e.target) && e.target.closest('[data-recommend-close]'))) {
        close();
        return;
      }

      if (!dom.overlay.contains(e.target)) return;

      // バリエーションのチップ
      const chip = e.target.closest('[data-recommend-variant]');
      if (chip) {
        const chipRow = chip.closest('[data-recommend-row]');
        if (chipRow) selectVariant(chipRow, chip);
        return;
      }

      // 数量ステッパー
      const minus = e.target.closest('[data-recommend-minus]');
      const plus = e.target.closest('[data-recommend-plus]');
      if (minus || plus) {
        const row = e.target.closest('[data-recommend-row]');
        if (!row) return;
        const variantId = row.getAttribute('data-variant-id');
        const current = selectedQty[variantId] || 0;
        selectedQty[variantId] = plus ? current + 1 : Math.max(0, current - 1);
        syncQuantities();
        return;
      }

      // カートに追加（モーダル下部に1つだけ）
      const addBtn = e.target.closest('[data-recommend-add]');
      if (addBtn && !addBtn.disabled) submitAll();
    });

    // バリエーションのプルダウン
    document.addEventListener('change', (e) => {
      if (!(e.target instanceof Element)) return;
      const select = e.target.closest('[data-recommend-variant-select]');
      if (!select) return;
      const selectRow = select.closest('[data-recommend-row]');
      const option = select.options[select.selectedIndex];
      if (selectRow && option) selectVariant(selectRow, option);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (dom.zoomOverlay && !dom.zoomOverlay.hidden) {
        closeZoom();
        return;
      }
      close();
    });

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        scheduleRefresh(Boolean(event && event.source === 'product-form'));
      });
    }

    /* --- テーマエディタ連携 ---
       設定を変更するたびにセクションが再描画され、そのつど選択イベントが発火します。
       毎回モーダルが開くと編集の邪魔になるため、開くかどうかはセクション設定で切り替えます。 */
    const isOwnSection = (el) => Boolean(el && el.querySelector && el.querySelector('[data-recommend-wrap]'));
    // 開くかどうかはセクション設定で切り替える
    const editorAutoOpen = () => Boolean(config.editorPreviewOpen);

    // DOM参照の取り直しは設定に関係なく常に必要
    document.addEventListener('shopify:section:load', (e) => {
      if (isOwnSection(e.target)) setup();
    });

    document.addEventListener('shopify:section:select', (e) => {
      if (editorAutoOpen() && isOwnSection(e.target)) open();
    });

    document.addEventListener('shopify:section:deselect', (e) => {
      if (editorAutoOpen() && isOwnSection(e.target)) close();
    });

    document.addEventListener('shopify:block:select', (e) => {
      if (!editorAutoOpen()) return;
      // ブロックのイベントは shopify_attributes を付けた要素で発火します
      const blockEl = e.target && e.target.closest && e.target.closest('[data-recommend-block]');
      if (!blockEl) return;
      open();
      blockEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    document.addEventListener('shopify:block:deselect', (e) => {
      if (!editorAutoOpen()) return;
      const blockEl = e.target && e.target.closest && e.target.closest('[data-recommend-block]');
      if (blockEl) close();
    });
  }

  /* -------------------------------------------------
   * 初期化
   * ------------------------------------------------- */
  function setup() {
    config = window.RecommendConfig || {};

    // 枠が0件でHTMLが無い場合でも、テーマエディタで枠が追加された瞬間に
    // 気づけるよう、監視系だけは先に登録しておく。
    installNetworkHooks();
    installListeners();

    dom.wrap = document.querySelector('[data-recommend-wrap]');
    dom.overlay = document.querySelector('[data-recommend-overlay]');
    if (!dom.wrap || !dom.overlay) return;

    dom.fab = dom.wrap.querySelector('[data-recommend-fab]');
    dom.blocks = Array.from(dom.overlay.querySelectorAll('[data-recommend-block]'));
    dom.addBtn = dom.overlay.querySelector('[data-recommend-add]');
    dom.cartLink = dom.overlay.querySelector('[data-recommend-cart-link]');
    // 完了表示から元に戻すときのために、テーマ設定のラベルを控えておく
    if (dom.addBtn && !dom.addBtn.dataset.defaultLabel) {
      dom.addBtn.dataset.defaultLabel = dom.addBtn.textContent.trim();
    }
    dom.zoomOverlay = document.querySelector('[data-recommend-zoom-overlay]');
    dom.zoomImage = document.querySelector('[data-recommend-zoom-image]');

    // トリガー判定を O(1) にするため、ブロックIDごとに Set 化しておく
    triggerMap = new Map(
      (config.blocks || []).map((block) => [block.id, { type: block.triggerType, ids: new Set(block.triggerIds || []) }])
    );

    render(); // 既知のカート状態で即描画してから、最新を取り直す
    fetchCart().then((cart) => {
      if (cart) applyCart(cart);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
